import { MouseEvent } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Photo } from '@watchduty/camera';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import CameraIcon from 'assets/camera.svg?react';
import { getDeviceInfo } from 'state/localStorageTyped';
import { useTakePhoto } from 'components/FilePicker';

type FilePickerProps = {
  message: string;
  onFilesPicked: (files: File | File[] | Photo) => void;
  onFilesInvalid: (error: string) => void;
  /** Called right before the native camera Activity launches. */
  onBeforeCapture?: () => void;
  /** Called when the native camera settles without a photo (cancelled or failed). */
  onCaptureCancelled?: () => void;
  error?: string;
  disabled?: boolean;
  minHeight?: number;
};

const MAX_FILE_UPLOAD_BYTES = 5000000; // 5 MB
const ALLOWED_MIMETYPES = 'image/*';

const getBorderColor = (theme: Theme, hasError: boolean): string => {
  if (hasError) return theme.palette.error.main;
  return theme.palette.divider;
};

const getCardSx = (args: {
  theme: Theme;
  disabled?: boolean;
  hasError: boolean;
  minHeight?: number;
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}) => {
  const { theme, disabled, hasError, minHeight = 115 } = args;
  return {
    backgroundColor: theme.palette.warmGrey[100],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.warmGrey[900],
    }),
    borderColor: getBorderColor(theme, hasError),
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex' as const,
    minHeight,
    p: 3,
    borderRadius: 2,
  };
};

export const FilePicker = (props: FilePickerProps): JSX.Element => {
  const {
    message,
    onFilesPicked,
    onFilesInvalid,
    onBeforeCapture,
    onCaptureCancelled,
    error,
    disabled,
    minHeight,
  } = props;
  const deviceInfo = getDeviceInfo();
  const takePhoto = useTakePhoto();
  const { t } = useTranslation();

  const handleDropRejected = (dropErrors: FileRejection[]): void => {
    // convert the list of react-dropzone errors into a single human-readable error string
    const errorCodes = dropErrors
      .map((drop) => drop.errors.map((e) => e.code))
      .flat();

    const DROP_ERROR_CODE_TO_TEXT: Record<FileError['code'], string> = {
      'file-too-large': t('filePicker.errors.tooLarge'),
      'file-invalid-type': t('filePicker.errors.invalidType'),
      default: t('filePicker.errors.default'),
    };

    onFilesInvalid(DROP_ERROR_CODE_TO_TEXT[errorCodes[0]]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: ALLOWED_MIMETYPES,
    onDropAccepted: (files: File[]) => onFilesPicked(files),
    onDropRejected: handleDropRejected,
    maxSize: MAX_FILE_UPLOAD_BYTES,
  });

  const handleNativeClick = async (): Promise<void> => {
    // Must run before getPhoto(). The process can be killed once the camera Activity takes over.
    onBeforeCapture?.();

    const image = await takePhoto();
    if (!image) {
      onCaptureCancelled?.();
      return;
    }
    onFilesPicked(image);
  };

  const handleClick = async (event: MouseEvent<HTMLElement>): Promise<void> => {
    if (deviceInfo?.isWeb) {
      // defer to useDropzone()
      return;
    }
    event.stopPropagation();
    await handleNativeClick();
  };

  const hasError = Boolean(error);

  return (
    <Card
      variant="outlined"
      sx={[
        (theme) =>
          getCardSx({
            theme,
            disabled,
            hasError,
            minHeight,
          }),
      ]}
      {...getRootProps({
        onClick: handleClick,
        role: 'button',
        'aria-label': t('photoSubmission.form.inputs.photo.ariaLabel'),
      })}
    >
      <input {...getInputProps()} disabled={disabled} />

      <Stack
        justifyContent="center"
        alignItems="center"
        spacing={1}
        sx={{ flex: 1 }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            '& svg': {
              width: '100%',
              height: '100%',
              color: 'text.secondary',
            },
          }}
        >
          <CameraIcon aria-hidden />
        </Box>

        <Typography
          variant="body1"
          align="center"
          fontWeight="fontWeightMedium"
        >
          {message}
        </Typography>
      </Stack>
    </Card>
  );
};
